import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  Alert
} from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { DraggableDialog } from '../../../shared/components/DraggableDialog';
import { useSectionStyles } from '@/styles/utilities/hooks';
import api from '../../../shared/services/api';
import { API_ENDPOINTS } from '../../../shared/constants/apiEndpoints';
import { ResiduesVisualization, groupResiduesByChain } from './ResidueVisualization';

// Helper function to generate unique cluster name
const generateUniqueClusterName = (baseName, existingClusters) => {
  // Get all existing cluster names
  const existingNames = existingClusters.map(c => c.name).filter(Boolean);
  
  // If base name doesn't exist, use it
  if (!existingNames.includes(baseName)) {
    return baseName;
  }
  
  // Otherwise, append numbers until we find a unique name
  let counter = 2;
  let uniqueName = `${baseName}_${counter}`;
  
  while (existingNames.includes(uniqueName)) {
    counter++;
    uniqueName = `${baseName}_${counter}`;
  }
  
  return uniqueName;
};

const ManualSetEditorDialog = ({
  open,
  onClose,
  sourceMode, // 'from-clusterspec' or 'from-existing'
  sourceClusterId,
  clustersWithTargets,
  fileAnalysis,
  onCreateManualSet,
  selectedRows = []
}) => {
  const theme = useTheme();
  const sectionStyles = useSectionStyles();
  
  // Dialog state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Cluster details
  const [clusterDetails, setClusterDetails] = useState(null);
  const [selectedResidues, setSelectedResidues] = useState(new Set());
  const [manualName, setManualName] = useState('');
  const [manualDescription, setManualDescription] = useState('');
  
  // Load cluster details when dialog opens or source changes
  useEffect(() => {
    if (open && sourceClusterId) {
      loadClusterDetails();
    }
  }, [open, sourceClusterId]);
  
  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setError('');
      setClusterDetails(null);
      setSelectedResidues(new Set());
      setManualName('');
      setManualDescription('');
    }
  }, [open]);
  
  const loadClusterDetails = async () => {
    setLoading(true);
    try {
      // Get cluster details from API
      const response = await api.get(API_ENDPOINTS.CLUSTERS.BY_ID(sourceClusterId));
      const cluster = response.data;
      setClusterDetails(cluster);
      
      // Find the cluster in the list to get additional info
      const clusterInfo = clustersWithTargets.find(c => c.id_ === sourceClusterId);
      if (clusterInfo) {
        cluster.residueCount = clusterInfo.residueCount;
        cluster.target = clusterInfo.target;
      }
      
      // Initialize all residues as selected based on actual residue array
      if (cluster.residues && Array.isArray(cluster.residues)) {
        const allResidueIndices = new Set(
          Array.from({ length: cluster.residues.length }, (_, i) => i)
        );
        setSelectedResidues(allResidueIndices);
      } else {
        setSelectedResidues(new Set());
      }
      
      // Both cases create a manual set - use consistent naming
      const suggestedName = `${cluster.name}_manual`;
      const description = sourceMode === 'from-clusterspec' 
        ? `Manual selection from ${cluster.name} (cutoff rule)`
        : `Manual selection from ${cluster.name}`;
      setManualDescription(description);
      
      // Ensure uniqueness
      const uniqueName = generateUniqueClusterName(suggestedName, clustersWithTargets);
      setManualName(uniqueName);
    } catch (err) {
      console.error('Failed to load cluster details:', err);
      setError('Failed to load residue set details');
    } finally {
      setLoading(false);
    }
  };
  
  const handleResidueToggle = (residueIndex) => {
    const newSelection = new Set(selectedResidues);
    if (newSelection.has(residueIndex)) {
      newSelection.delete(residueIndex);
    } else {
      newSelection.add(residueIndex);
    }
    setSelectedResidues(newSelection);
  };
  
  const handleToggleAll = (selectAll) => {
    if (selectAll) {
      // Select all residues
      const allResidueIndices = new Set(
        Array.from({ length: clusterDetails?.residues?.length || 0 }, (_, i) => i)
      );
      setSelectedResidues(allResidueIndices);
    } else {
      // Deselect all
      setSelectedResidues(new Set());
    }
  };
  
  const handleToggleChain = (chain, selectAll) => {
    if (!clusterDetails?.residues) return;
    
    const groupedByChain = groupResiduesByChain(clusterDetails.residues);
    const chainData = groupedByChain[chain];
    
    if (!chainData) return;
    
    // Extract all residues from this chain (both regular and insertions)
    const chainResidues = [
      ...Object.values(chainData.regular),
      ...Object.values(chainData.insertions).flat()
    ];
    
    const newSelection = new Set(selectedResidues);
    
    chainResidues.forEach(residue => {
      if (selectAll) {
        newSelection.add(residue.index);
      } else {
        newSelection.delete(residue.index);
      }
    });
    
    setSelectedResidues(newSelection);
  };
  
  const handleCreate = async () => {
    setLoading(true);
    setError('');
    
    try {
      if (!manualName.trim()) {
        setError('Name is required');
        setLoading(false);
        return;
      }
      
      if (selectedResidues.size === 0) {
        setError('At least one residue must be selected');
        setLoading(false);
        return;
      }
      
      // Get the actual residue strings for selected indices
      const selectedResidueStrings = Array.from(selectedResidues).map(index => 
        clusterDetails.residues[index]
      );
      
      await onCreateManualSet({
        name: manualName.trim(),
        description: manualDescription.trim(),
        sourceClusterId: sourceClusterId,
        sourceName: clusterDetails?.name,
        residues: selectedResidueStrings,  // Pass actual residue strings, not indices
        target: clusterDetails?.target
      });
      
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to create manual residue set');
    } finally {
      setLoading(false);
    }
  };
  
  const getDialogTitle = () => {
    return 'Create Manual Residue Set';
  };
  
  
  return (
    <DraggableDialog
      open={open}
      onClose={onClose}
      title={getDialogTitle()}
      variant="details"
      maxWidth="md"
      fullWidth
    >
      {loading && !clusterDetails ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Name and Description Fields */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1.5), mb: theme.spacing(1) }}>
            {/* Manual Set Name Field */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
              <Typography variant="caption" sx={sectionStyles.sectionHeader}>
                MANUAL SET NAME
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
                required
                placeholder="Enter manual set name"
                sx={{ 
                  '& .MuiInputBase-input': { fontSize: theme.typography.fontSize.base }
                }}
              />
            </Box>
            
            {/* Description Field */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
              <Typography variant="caption" sx={sectionStyles.sectionHeader}>
                DESCRIPTION
              </Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={3}
                value={manualDescription}
                onChange={(e) => setManualDescription(e.target.value)}
                placeholder="Enter description..."
                sx={{ 
                  '& .MuiInputBase-input': { fontSize: theme.typography.fontSize.base }
                }}
              />
            </Box>
          </Box>
          
          {/* Residue Selection */}
          {clusterDetails && clusterDetails.residues && clusterDetails.residues.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, mt: theme.spacing(1), gap: theme.spacing(1) }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" sx={sectionStyles.sectionHeader}>
                  RESIDUES ({selectedResidues.size}/{clusterDetails.residues.length}) - click to toggle
                </Typography>
                <Box sx={{ display: 'flex', gap: theme.spacing(0.5) }}>
                  <IconButton 
                    size="small" 
                    onClick={() => handleToggleAll(true)}
                    title="Select All"
                    sx={{ p: theme.spacing(0.25) }}
                  >
                    <Check sx={{ fontSize: theme.dimensions?.icon?.tiny || 14 }} />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => handleToggleAll(false)}
                    title="Deselect All"
                    sx={{ p: theme.spacing(0.25) }}
                  >
                    <Close sx={{ fontSize: theme.dimensions?.icon?.tiny || 14 }} />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ 
                flex: 1,
                overflow: 'auto',
                border: '1px solid',
                borderColor: theme.palette.divider,
                borderRadius: theme.spacing(0.5),
                p: theme.spacing(1.5),
                backgroundColor: theme.palette.background.paper,
                '&::-webkit-scrollbar': {
                  width: theme.spacing(1),
                  height: theme.spacing(1)
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: theme.palette.grey[100]
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: theme.palette.grey[400],
                  borderRadius: theme.spacing(0.5),
                  '&:hover': {
                    backgroundColor: theme.palette.grey[500]
                  }
                }
              }}>
                <ResiduesVisualization
                  residues={clusterDetails.residues}
                  selectedSet={selectedResidues}
                  onToggle={handleResidueToggle}
                  isEditable={true}
                  onToggleAll={null}
                  onToggleChain={handleToggleChain}
                />
              </Box>
            </Box>
          )}
          
          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mt: theme.spacing(1), fontSize: theme.typography.fontSize.sm }}>
              {error}
            </Alert>
          )}
          
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing(1), mt: theme.spacing(2) }}>
            <Button 
              onClick={onClose} 
              disabled={loading}
              size="medium"
              sx={{ 
                fontSize: theme.typography.fontSize.base,
                px: theme.spacing(2),
                minWidth: 'auto'
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleCreate}
              disabled={loading || !manualName.trim() || selectedResidues.size === 0}
              size="medium"
              startIcon={loading ? <CircularProgress size={16} /> : null}
              sx={{ 
                fontSize: theme.typography.fontSize.base,
                px: theme.spacing(2),
                minWidth: 'auto'
              }}
            >
              {loading ? 'Creating...' : 'Create Manual Set'}
            </Button>
          </Box>
        </Box>
      )}
    </DraggableDialog>
  );
};

export default ManualSetEditorDialog;